import React, { useState, useEffect } from 'react';

const NoticeList = () => {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: 'All',
    priority: 'All'
  });

  useEffect(() => {
    // Mock data for notices
    const mockNotices = [
      {
        id: 1,
        title: 'Holiday Notice for Christmas',
        description: 'The school will remain closed on December 25th for Christmas holiday.',
        category: 'General',
        priority: 'Important',
        publishedBy: 'Admin',
        createdAt: '2023-12-10',
        views: 1250,
        isActive: true
      },
      {
        id: 2,
        title: 'Exam Schedule Released',
        description: 'Final term exam schedule for all classes has been released. Please check the notice board.',
        category: 'Exam',
        priority: 'Urgent',
        publishedBy: 'Admin',
        createdAt: '2023-12-08',
        views: 2100,
        isActive: true
      },
      {
        id: 3,
        title: 'Parent Teacher Meeting',
        description: 'Quarterly parent teacher meeting scheduled for December 15th at 3 PM.',
        category: 'Academic',
        priority: 'Normal',
        publishedBy: 'Admin',
        createdAt: '2023-12-05',
        views: 890,
        isActive: true
      }
    ];
    
    setNotices(mockNotices);
    setLoading(false);
  }, []);

  const filteredNotices = notices.filter(notice => {
    return (
      (filters.category === 'All' || notice.category === filters.category) &&
      (filters.priority === 'All' || notice.priority === filters.priority)
    );
  });

  const categories = ['All', 'Academic', 'Admission', 'Exam', 'General', 'Events'];
  const priorities = ['All', 'Normal', 'Important', 'Urgent'];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800">Manage Notices</h1>
          <a 
            href="/admin/notices/create" 
            className="bg-primary text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition"
          >
            Add New Notice
          </a>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-gray-700 mb-2">Category</label>
              <select
                className="w-full p-2 border rounded"
                value={filters.category}
                onChange={(e) => setFilters({...filters, category: e.target.value})}
              >
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-gray-700 mb-2">Priority</label>
              <select
                className="w-full p-2 border rounded"
                value={filters.priority}
                onChange={(e) => setFilters({...filters, priority: e.target.value})}
              >
                {priorities.map(priority => (
                  <option key={priority} value={priority}>{priority}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button className="w-full bg-primary text-white px-4 py-2 rounded hover:bg-blue-700">
                Apply Filters
              </button>
            </div>
          </div>
        </div>

        {/* Notices Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Published By</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Views</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredNotices.map((notice) => (
                  <tr key={notice.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{notice.title}</div>
                      <div className="text-sm text-gray-500 line-clamp-1">{notice.description}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {notice.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        notice.priority === 'Urgent' ? 'bg-red-100 text-red-800' :
                        notice.priority === 'Important' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {notice.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{notice.publishedBy}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{notice.views}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{notice.createdAt}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        notice.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {notice.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                      <a href={`/admin/notices/edit/${notice.id}`} className="text-blue-600 hover:text-blue-900">Edit</a>
                      <a href={`/admin/notices/view/${notice.id}`} className="text-gray-600 hover:text-gray-900 ml-2">View</a>
                      <button className="text-red-600 hover:text-red-900 ml-2">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NoticeList;